import React, { useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import { Icon } from './Icon';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { Event } from '../../../types/event.types';
import './AddToCalendarButton.css';

interface AddToCalendarButtonProps {
  event: Event;
  /** Context where the button is used (for analytics) */
  analyticsContext?: 'card' | 'carte' | 'modale' | 'carte_preview';
  /** Button variant */
  variant?: 'glass' | 'ghost' | 'primary' | 'secondary';
  /** Icon size */
  iconSize?: number;
  className?: string;
}

/** Desktop breakpoint */
const DESKTOP_BREAKPOINT = 1024;

/**
 * Formats a Date object to ICS date format (YYYYMMDDTHHMMSS)
 */
const formatDateToICS = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
};

/**
 * Formats a Date to UTC format for Google Calendar (YYYYMMDDTHHMMSSZ)
 */
const formatDateToUTC = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Escapes special characters for ICS format
 */
const escapeICSText = (text: string): string => {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
};

/**
 * Calculates end time from start time and endTime string (HH:MM format)
 * or adds default duration of 2 hours
 */
const calculateEndDate = (startDate: Date, endTime?: string, endDateTime?: string): Date => {
  // If we have endDateTime, parse it
  if (endDateTime) {
    const parsed = new Date(endDateTime);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // If we have endTime (HH:MM format), use it with the same date
  if (endTime) {
    const [hours, minutes] = endTime.split(':').map(Number);
    const endDate = new Date(startDate);
    endDate.setHours(hours, minutes, 0, 0);

    // If end time is before start time, it's the next day
    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    return endDate;
  }

  // Default: add 2 hours
  const endDate = new Date(startDate);
  endDate.setHours(endDate.getHours() + 2);
  return endDate;
};

/**
 * Generates ICS file content for the event
 */
const generateICSContent = (event: Event): string => {
  if (!event.datetime) {
    throw new Error('Event must have a datetime');
  }

  const startDate = new Date(event.datetime);
  const endDate = calculateEndDate(startDate, event.endTime, event.endDateTime);

  const title = escapeICSText(event.title);
  const description = event.description ? escapeICSText(event.description) : '';
  const location = event.locationName || event.location || '';
  const escapedLocation = escapeICSText(location);

  // Add coordinates to location if available
  const geoLocation = event.latitude && event.longitude
    ? `GEO:${event.latitude};${event.longitude}\n`
    : '';

  // Generate unique ID for the event
  const uid = `${event.id}@what2do.today`;

  // Current timestamp for DTSTAMP
  const now = formatDateToICS(new Date());

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//What2Do.Today//Event Calendar//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${now}
DTSTART:${formatDateToICS(startDate)}
DTEND:${formatDateToICS(endDate)}
SUMMARY:${title}
${description ? `DESCRIPTION:${description}\n` : ''}LOCATION:${escapedLocation}
${geoLocation}URL:${window.location.origin}/event/${event.id}
END:VEVENT
END:VCALENDAR`;

  return icsContent;
};

/**
 * Check if Web Share API supports sharing files
 */
const canShareFiles = (): boolean => {
  if (typeof navigator === 'undefined' || !navigator.share || !navigator.canShare) {
    return false;
  }
  // Test if we can share files
  const testFile = new File(['test'], 'test.ics', { type: 'text/calendar' });
  return navigator.canShare({ files: [testFile] });
};

/**
 * Opens ICS file directly - uses Web Share API on mobile for native app picker
 */
const openCalendarWithEvent = async (event: Event): Promise<void> => {
  const icsContent = generateICSContent(event);
  const fileName = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;

  // Create a File object (not just Blob) for better compatibility
  const file = new File([icsContent], fileName, {
    type: 'text/calendar;charset=utf-8'
  });

  // Try Web Share API first (best mobile experience - shows native app picker)
  if (canShareFiles()) {
    try {
      await navigator.share({
        files: [file],
        title: event.title,
        text: `Add "${event.title}" to your calendar`
      });
      return; // Success!
    } catch (err) {
      // User cancelled or error - fall through to fallback
      if ((err as Error).name === 'AbortError') {
        return; // User cancelled, don't try fallback
      }
      console.log('Web Share failed, trying fallback:', err);
    }
  }

  // Fallback: Open blob URL without download attribute
  // This lets the OS handle the .ics MIME type natively (opens calendar app picker)
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  window.location.href = url;

  // Clean up after a longer delay to let the OS process the file
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/**
 * Downloads the ICS file
 */
const downloadICSFile = (event: Event): void => {
  const icsContent = generateICSContent(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generates Google Calendar URL
 */
const generateGoogleCalendarUrl = (event: Event): string => {
  if (!event.datetime) return '';

  const startDate = new Date(event.datetime);
  const endDate = calculateEndDate(startDate, event.endTime, event.endDateTime);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatDateToUTC(startDate)}/${formatDateToUTC(endDate)}`,
    details: event.description || '',
    location: event.locationName || event.location || '',
    sprop: `website:${window.location.origin}`
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generates Outlook.com Calendar URL
 */
const generateOutlookUrl = (event: Event): string => {
  if (!event.datetime) return '';

  const startDate = new Date(event.datetime);
  const endDate = calculateEndDate(startDate, event.endTime, event.endDateTime);

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
    body: event.description || '',
    location: event.locationName || event.location || ''
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Check if user is on macOS
 */
const isMacOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0 ||
    navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
};

/**
 * Check if we're on desktop
 */
const useIsDesktop = (): boolean => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches);
    };

    // Set initial value
    setIsDesktop(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isDesktop;
};

interface DropdownPosition {
  top: number;
  left: number;
}

const AddToCalendarButton: React.FC<AddToCalendarButtonProps> = ({
  event,
  analyticsContext = 'card',
  variant = 'glass',
  iconSize = 20,
  className
}) => {
  const analytics = useAnalytics();
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap below button
        left: rect.right - 180 // Align right edge (180px = min-width of dropdown)
      });
    }
  }, [isOpen]);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    if (!event.datetime) {
      console.warn('Cannot add to calendar: event has no datetime');
      return;
    }

    // On desktop, toggle the dropdown menu
    if (isDesktop) {
      setIsOpen(prev => !prev);
      return;
    }

    // On mobile: download .ics file — the OS handles it natively
    // iOS Safari: opens "Add to Calendar" native dialog
    // Android: opens default calendar app or asks which app to use
    analytics.trackNavigation(event, 'calendar', analyticsContext);
    downloadICSFile(event);
  }, [event, analytics, analyticsContext, isDesktop]);

  const handleGoogleCalendar = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    analytics.trackNavigation(event, 'calendar', analyticsContext);
    window.open(generateGoogleCalendarUrl(event), '_blank', 'noopener,noreferrer');
  }, [event, analytics, analyticsContext]);

  const handleOutlook = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    analytics.trackNavigation(event, 'calendar', analyticsContext);
    window.open(generateOutlookUrl(event), '_blank', 'noopener,noreferrer');
  }, [event, analytics, analyticsContext]);

  const handleDownloadICS = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    analytics.trackNavigation(event, 'calendar', analyticsContext);
    downloadICSFile(event);
  }, [event, analytics, analyticsContext]);

  const handleAppleCalendar = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    analytics.trackNavigation(event, 'calendar', analyticsContext);
    // Open directly - macOS will handle it with Calendar.app
    await openCalendarWithEvent(event);
  }, [event, analytics, analyticsContext]);

  const isOnMac = isMacOS();

  // Don't render if event has no datetime
  if (!event.datetime) {
    return null;
  }

  const dropdownMenu = isDesktop && isOpen && createPortal(
    <div
      ref={dropdownRef}
      className="calendar-button__dropdown"
      role="menu"
      style={{
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left
      }}
    >
      <button
        className="calendar-button__item"
        onClick={handleGoogleCalendar}
        role="menuitem"
      >
        <Icon name="Calendar" size={18} />
        <span>Google Calendar</span>
      </button>
      <button
        className="calendar-button__item"
        onClick={handleOutlook}
        role="menuitem"
      >
        <Icon name="Mail" size={18} />
        <span>Outlook</span>
      </button>
      <div className="calendar-button__separator" />
      {isOnMac ? (
        <button
          className="calendar-button__item"
          onClick={handleAppleCalendar}
          role="menuitem"
        >
          <Icon name="CalendarDays" size={18} />
          <span>Apple Calendar</span>
        </button>
      ) : (
        <button
          className="calendar-button__item"
          onClick={handleDownloadICS}
          role="menuitem"
        >
          <Icon name="Download" size={18} />
          <span>Download .ics</span>
        </button>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={containerRef} className="calendar-button" onClick={(e) => e.stopPropagation()}>
      <Button
        variant={variant}
        onClick={handleClick}
        aria-label="Add to calendar"
        aria-expanded={isDesktop ? isOpen : undefined}
        aria-haspopup={isDesktop ? 'true' : undefined}
        className={className}
      >
        <Icon name="CalendarPlus" size={iconSize} />
      </Button>

      {/* Desktop dropdown menu (rendered via portal) */}
      {dropdownMenu}
    </div>
  );
};

export default React.memo(AddToCalendarButton);
